import 'swiper/css/bundle';

import Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';

const CIRCUMFERENCE = 94.25;

// Swiper updates `activeIndex` before firing `slideChange`, whereas the
// `swiper-slide-active` class is swapped afterwards -- so compare by index to
// keep the inert flag aligned with the visible slide on every transition.
const updateSlidesInert = (swiper: Swiper) => {
  swiper.slides.forEach((slide, index) => {
    if (index === swiper.activeIndex) {
      slide.removeAttribute('inert');
    } else {
      slide.setAttribute('inert', '');
    }
  });
};

const initHeroSliders = () => {
  document
    .querySelectorAll<HTMLElement>('.js-hero-slider-wrapper')
    .forEach((wrapper) => {
      const sliderEl = wrapper.querySelector<HTMLElement>('.js-hero-slider');
      if (!sliderEl) return;

      const autoplaySpeed = parseInt(
        sliderEl.dataset.autoplaySpeed ?? '5000',
        10
      );
      const paginationEl = sliderEl.querySelector<HTMLElement>(
        '.js-hero-slider-pagination'
      );
      const pauseBtn = wrapper.querySelector<HTMLButtonElement>(
        '.js-hero-slider-pause'
      );
      const ring = pauseBtn?.querySelector<SVGCircleElement>(
        '.js-hero-slider-ring'
      );
      const pauseIcon = pauseBtn?.querySelector<HTMLElement>('.js-pause-icon');
      const playIcon = pauseBtn?.querySelector<HTMLElement>('.js-play-icon');

      // Hint to browser to keep stroke-dashoffset on a composited layer
      if (ring) ring.style.willChange = 'stroke-dashoffset';

      let ringAnimation: Animation | null = null;

      const startRing = () => {
        ringAnimation?.cancel();
        // Force the element to acknowledge the reset before starting
        ring?.getBoundingClientRect();
        ringAnimation =
          ring?.animate(
            [{ strokeDashoffset: CIRCUMFERENCE }, { strokeDashoffset: 0 }],
            { duration: autoplaySpeed, easing: 'linear', fill: 'forwards' }
          ) ?? null;
      };

      const pauseRing = () => ringAnimation?.pause();
      const resumeRing = () => ringAnimation?.play();

      const swiper = new Swiper(sliderEl, {
        modules: [Autoplay, Pagination],
        slidesPerView: 1,
        loop: true,
        speed: 600,
        autoplay: {
          delay: autoplaySpeed,
          pauseOnMouseEnter: true,
          disableOnInteraction: false
        },
        pagination: paginationEl
          ? { el: paginationEl, clickable: true }
          : false,
        on: {
          init(instance) {
            if (paginationEl) paginationEl.style.bottom = '2rem';
            startRing();
            updateSlidesInert(instance);
          },
          // Wait until the slide transition finishes before restarting the ring
          // Skip if manually paused — don't restart the ring while paused
          slideChangeTransitionEnd() {
            if (pauseBtn?.dataset.paused !== 'true') startRing();
          },
          slideChange(instance) {
            updateSlidesInert(instance);
          },
          autoplayPause: pauseRing,
          autoplayResume: resumeRing
        }
      });

      if (!pauseBtn) return;

      pauseBtn.addEventListener('click', () => {
        const isPaused = pauseBtn.dataset.paused === 'true';
        if (isPaused) {
          swiper.autoplay.start();
          startRing();
          pauseBtn.dataset.paused = 'false';
          pauseBtn.setAttribute(
            'aria-label',
            pauseBtn.dataset.labelPause ?? ''
          );
          pauseIcon?.classList.remove('hidden');
          playIcon?.classList.add('hidden');
        } else {
          swiper.autoplay.stop();
          pauseRing();
          pauseBtn.dataset.paused = 'true';
          pauseBtn.setAttribute(
            'aria-label',
            pauseBtn.dataset.labelResume ?? ''
          );
          pauseIcon?.classList.add('hidden');
          playIcon?.classList.remove('hidden');
        }
      });
    });
};

initHeroSliders();
